import React, { useState, useEffect, useRef } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useDispatch, connect } from "react-redux";
import {
  Button,
  IconButton,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Paper,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import spinner from "../assets/images/loadingGif.gif";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import accessToken from "../helpers/jwt-token-access/accessToken";
import { jwtDecode } from "jwt-decode";
import {
  addUser,
  addUserError,
  getUser,
  updateCognitoUser,
  deleteCognitoUser,
} from "../store/actions";

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: "#193a6f",
  },
  activeButton: {
    color: "white",
    marginLeft: "auto",
    textDecoration: "none",
  },
  logo: {
    maxWidth: 160,
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    maxWidth: 500,
    width: "100%",
    maxHeight: "90vh",
    overflow: "auto",
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  passwordRequirements: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
  },
  requirementItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
    fontSize: "0.875rem",
  },
  requirementMet: {
    color: theme.palette.success.main,
  },
  requirementNotMet: {
    color: theme.palette.error.main,
  },
  errorContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  page: {
    padding: theme.spacing(3),
    width: "100%",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: {
    margin: 0,
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#193a6f",
  },
  tablePaper: {
    width: "100%",
    overflow: "auto",
  },
  dataGrid: {
    border: "none",
    "& .MuiDataGrid-toolbarContainer": {
      position: "relative",
    },
    "& .MuiDataGrid-toolbarQuickFilter": {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
      paddingBottom: 0,
      "& .MuiInputBase-root": {
        color: "#193a6f",
      },
      "& .MuiInputBase-input": {
        color: "#193a6f",
      },
      "& .MuiInputBase-input::placeholder": {
        color: "#193a6f",
        opacity: 0.7,
      },
      "& .MuiSvgIcon-root": {
        color: "#193a6f",
      },
      "& .MuiIconButton-root": {
        color: "#193a6f",
      },
      "& .MuiInput-underline:before": {
        borderBottomColor: "#193a6f",
      },
      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
        borderBottomColor: "#193a6f",
      },
      "& .MuiInput-underline:after": {
        borderBottomColor: "#193a6f",
      },
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#193a6f",
      color: "#ffffff",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 600,
      color: "#ffffff",
    },
    "& .MuiDataGrid-iconSeparator": {
      color: "rgba(255, 255, 255, 0.4)",
    },
    "& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton, & .MuiDataGrid-columnHeader .MuiSvgIcon-root": {
      color: "#ffffff",
    },
    "& .MuiDataGrid-cell": {
      fontSize: "0.875rem",
    },
    "& .MuiDataGrid-row:hover": {
      backgroundColor: "#f8fafc",
    },
    "& .MuiDataGrid-footerContainer": {
      minHeight: 56,
      borderTop: "1px solid #193a6f",
      backgroundColor: "#193a6f",
      padding: "4px 16px",
      flexWrap: "wrap",
      justifyContent: "flex-end",
      color: "#ffffff",
      [theme.breakpoints.down("sm")]: {
        justifyContent: "center",
        minHeight: "auto",
        padding: theme.spacing(1),
      },
    },
    "& .MuiTablePagination-root": {
      color: "#ffffff",
      overflow: "visible",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
    "& .MuiTablePagination-toolbar": {
      minHeight: 52,
      paddingLeft: 0,
      paddingRight: 0,
      flexWrap: "wrap",
      gap: 8,
      [theme.breakpoints.down("sm")]: {
        justifyContent: "center",
        minHeight: "auto",
        paddingTop: 8,
        paddingBottom: 8,
      },
    },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      fontSize: "0.875rem",
      fontWeight: 500,
      margin: 0,
      color: "#ffffff",
    },
    "& .MuiTablePagination-input": {
      marginLeft: 8,
      marginRight: 16,
    },
    "& .MuiTablePagination-select": {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: "#ffffff",
      paddingRight: "28px !important",
      paddingLeft: "8px",
      paddingTop: "4px",
      paddingBottom: "4px",
      borderRadius: 4,
      border: "1px solid rgba(255, 255, 255, 0.5)",
      backgroundColor: "transparent",
    },
    "& .MuiTablePagination-selectIcon": {
      color: "#ffffff",
      right: 4,
      pointerEvents: "none",
    },
    "& .MuiTablePagination-actions": {
      marginLeft: 8,
    },
    "& .MuiTablePagination-actions .MuiIconButton-root": {
      color: "#ffffff",
      padding: 8,
      "&.Mui-disabled": {
        color: "rgba(255, 255, 255, 0.35)",
      },
    },
  },
}));

const Users = (props) => {
  const classes = useStyles();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  const [editedRowData, setEditedRowData] = useState(null);
  const [editFirstNameError, setEditFirstNameError] = useState("");
  const [editLastNameError, setEditLastNameError] = useState("");
  const [editEmailError, setEditEmailError] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const token = accessToken();
  const decoded = jwtDecode(token);
  const userName = decoded["email"];
  const userRole = decoded["cognito:groups"];

  const dispatch = useDispatch();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isFirstRender = useRef(true);

  // Password validation function
  const validatePassword = (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[~!@#$%^&*()_+\-={}\[\]|\\:;"'<>?,./]/.test(password),
    };

    const isValid = Object.values(requirements).every(Boolean);
    return { isValid, requirements };
  };

  // Password requirements component
  const PasswordRequirements = ({ password }) => {
    const { requirements } = validatePassword(password);

    return (
      <Box className={classes.passwordRequirements}>
        <Typography variant="subtitle2" gutterBottom>
          Password Requirements:
        </Typography>
        <div
          className={`${classes.requirementItem} ${
            requirements.minLength
              ? classes.requirementMet
              : classes.requirementNotMet
          }`}
        >
          ✓ Minimum 8 characters
        </div>
        <div
          className={`${classes.requirementItem} ${
            requirements.hasUppercase
              ? classes.requirementMet
              : classes.requirementNotMet
          }`}
        >
          ✓ Contains at least 1 uppercase letter
        </div>
        <div
          className={`${classes.requirementItem} ${
            requirements.hasLowercase
              ? classes.requirementMet
              : classes.requirementNotMet
          }`}
        >
          ✓ Contains at least 1 lowercase letter
        </div>
        <div
          className={`${classes.requirementItem} ${
            requirements.hasNumber
              ? classes.requirementMet
              : classes.requirementNotMet
          }`}
        >
          ✓ Contains at least 1 number
        </div>
        <div
          className={`${classes.requirementItem} ${
            requirements.hasSpecialChar
              ? classes.requirementMet
              : classes.requirementNotMet
          }`}
        >
          ✓ Contains at least 1 special character
        </div>
      </Box>
    );
  };

  useEffect(() => {
    if (userRole.includes("Admin")) {
      props.getUser();
    }
  }, []);

  console.log("Component rerendered with props = ", props);
  const [anchorEl, setAnchorEl] = useState(null);

  const columns = [
    {
      field: "firstName",
      headerName: "First Name",
      flex: 1,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "lastName",
      headerName: "Last Name",
      flex: 1,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "groupname",
      headerName: "Group Name",
      flex: 0.8,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.6,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "userCreatedDate",
      headerName: "Created Date",
      flex: 1.2,
      minWidth: 180,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "userModifiedDate",
      headerName: "Modified Date",
      flex: 1.2,
      minWidth: 180,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "email",
      headerName: "Username",
      flex: 1.5,
      minWidth: 200,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "edit",
      headerName: "Edit",
      flex: 0.4,
      minWidth: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (userRole.includes("Admin")) {
          return (
            <Tooltip title="Edit user">
              <IconButton
                color="primary"
                onClick={() => handleEdit(params.row)}
                aria-label="Edit user"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          );
        } else {
          return (
            <IconButton color="primary" disabled className="Mui-disabled">
              <EditIcon />
            </IconButton>
          );
        }
      },
    },
    {
      field: "delete",
      headerName: "Delete",
      flex: 0.4,
      minWidth: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (userRole.includes("Admin")) {
          return (
            <Tooltip title="Delete user">
              <IconButton
                color="secondary"
                onClick={() => {
                  console.log("params.row = ", params.row);
                  props.deleteUser(params.row.username);
                }}
                aria-label="Delete user"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          );
        } else {
          return (
            <IconButton color="secondary" disabled className="Mui-disabled">
              <DeleteIcon />
            </IconButton>
          );
        }
      },
    },
  ];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAnchor = () => {
    setAnchorEl(null);
  };

  const openAnchor = Boolean(anchorEl);
  const id = openAnchor ? "simple-popover" : undefined;

  // For Add user Modal
  const handleOpen = () => {
    setAddDialogOpen(true);
    // Reset form fields
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setGroupName("");
    setNameError("");
    setEmailError("");
    setPasswordError("");
    setShowErrorMessage(false);
    setErrorMessage("");
  };

  // For Add user Modal
  const handleClose = () => {
    setAddDialogOpen(false);
  };

  // For Add user Modal, change in email field
  const handleEmailChange = (event) => {
    const emailValue = event.target.value;
    setEmail(emailValue);

    // More permissive email validation that allows + and other valid characters
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailValue) {
      setEmailError("Please enter a valid email address");
    } else if (!emailRegex.test(emailValue)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  // Password change handler
  const handlePasswordChange = (event) => {
    const passwordValue = event.target.value;
    setPassword(passwordValue);

    const { isValid } = validatePassword(passwordValue);
    if (!passwordValue) {
      setPasswordError("Password is required");
    } else if (!isValid) {
      setPasswordError("Password does not meet requirements");
    } else {
      setPasswordError("");
    }
  };

  //For Submitting data of Add User to API
  const handleSubmit = (e) => {
    e.preventDefault();
    let valid = true;

    // Validate first and last name
    if (!firstName.trim()) {
      setNameError("Please enter your first name");
      valid = false;
    } else if (!lastName.trim()) {
      setNameError("Please enter your last name");
      valid = false;
    } else {
      setNameError("");
    }

    // Validate email
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email) {
      setEmailError("Please enter a valid email address");
      valid = false;
    } else if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      valid = false;
    } else {
      setEmailError("");
    }

    // Validate password
    const { isValid } = validatePassword(password);
    if (!password) {
      setPasswordError("Password is required");
      valid = false;
    } else if (!isValid) {
      setPasswordError("Password does not meet requirements");
      valid = false;
    } else {
      setPasswordError("");
    }

    // Validate group name
    if (!groupName) {
      valid = false;
    }

    if (valid) {
      const newUser = {
        firstname: firstName,
        lastname: lastName,
        email,
        password,
        groupName,
      };
      console.log("Handle Submit Called :: ", newUser);
      props.addUser(newUser);
    }
  };

  // For Edit user Modal, close the modal
  const handleEditClose = () => {
    setEditedRowData(null);
    setEditDialogOpen(false);
  };

  // For Edit user Modal, select the user
  const handleEdit = (rowData) => {
    console.log("rowData=", rowData);
    let row = { ...rowData, newgroupname: rowData.groupname };
    setEditedRowData(row);
    setEditDialogOpen(true);
  };

  // For Edit user Modal, change in email field
  const handleEditEmailChange = (event) => {
    const emailValue = event.target.value;
    setEditedRowData({
      ...editedRowData,
      email: emailValue,
    });

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailValue) {
      setEditEmailError("Please enter a valid email address");
    } else if (!emailRegex.test(emailValue)) {
      setEditEmailError("Please enter a valid email address");
    } else {
      setEditEmailError("");
    }
  };

  //For Submitting data of Edit User to API
  const handleUpdateUser = (e) => {
    e.preventDefault();
    let valid = true;

    if (!editedRowData.firstName?.trim()) {
      setEditFirstNameError("Please enter your first name");
      valid = false;
    } else {
      setEditFirstNameError("");
    }

    if (!editedRowData.lastName?.trim()) {
      setEditLastNameError("Please enter your last name");
      valid = false;
    } else {
      setEditLastNameError("");
    }

    // Check for valid email address
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!editedRowData.email) {
      setEditEmailError("Please enter a valid email address");
      valid = false;
    } else if (!emailRegex.test(editedRowData.email)) {
      setEditEmailError("Please enter a valid email address");
      valid = false;
    } else {
      setEditEmailError("");
    }

    console.log("editedRowData=", editedRowData);
    if (valid) {
      let updatedUser = {
        firstName: editedRowData.firstName,
        lastName: editedRowData.lastName,
        groupname: editedRowData.groupname,
        username: editedRowData.username,
      };
      if (editedRowData.groupname !== editedRowData.newgroupname) {
        updatedUser = {
          ...updatedUser,
          newgroupname: editedRowData.newgroupname,
        };
      }
      console.log("updatedUser=", updatedUser);
      props.updateUser(updatedUser);
    }
  };

  // Close Add dialog automatically and refresh user list
  useEffect(() => {
    console.log("useEffect for closing add dialog");
    if (!props.isAddingUser && !props.addUserError) {
      setAddDialogOpen(false);
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
      // Refresh the user list after successful add
      props.getUser();
    }
  }, [props.isAddingUser, props.addUserError]);

  // Close Edit dialog automatically and refresh user list
  useEffect(() => {
    console.log("useEffect for closing edit dialog");
    if (!props.isUpdatingUser && !props.updateUserError) {
      setEditDialogOpen(false);
      setEditedRowData(null);
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
      // Refresh the user list after successful update
      props.getUser();
    }
  }, [props.isUpdatingUser, props.updateUserError]);

  // Handle delete success and refresh user list
  useEffect(() => {
    if (!props.isUserDeleting && !props.deleteUserError) {
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
      // Refresh the user list after successful delete
      props.getUser();
    }
  }, [props.isUserDeleting, props.deleteUserError]);

  // Handle errors
  useEffect(() => {
    if (props.addUserError) {
      setShowErrorMessage(true);
      setErrorMessage("Error while adding user. Please try again.");
      setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage("");
      }, 5000);
    }

    if (props.updateUserError) {
      setShowErrorMessage(true);
      setErrorMessage("Error while updating user. Please try again.");
      setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage("");
      }, 5000);
    }

    if (props.deleteUserError) {
      setShowErrorMessage(true);
      setErrorMessage("Error while deleting user. Please try again.");
      setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage("");
      }, 5000);
    }

    if (props.getUsersError) {
      setShowErrorMessage(true);
      setErrorMessage("Error while loading users. Please refresh the page.");
      setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage("");
      }, 5000);
    }
  }, [
    props.addUserError,
    props.updateUserError,
    props.deleteUserError,
    props.getUsersError,
  ]);

  useEffect(() => {
    if (isFirstRender.current) {
      console.log("First Rendered");
      isFirstRender.current = false;
    } else {
      if (props.users) {
        if (props.users.length > 0) {
          setTimeout(() => {
            setShowSuccessMessage(false);
          }, 5000);
        }
      }
    }
  }, [props.users]);

  return (
    <div className={classes.page}>
      {userRole.includes("Admin") ? (
        <div>
          <div className={classes.header}>
            <h1 className={classes.title}>Manage Cognito Users</h1>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleIcon />}
              onClick={handleOpen}
            >
              Add User
            </Button>
          </div>

          {/* Success and Error Messages */}
          {showSuccessMessage && (
            <Alert severity="success" style={{ marginBottom: 16 }}>
              Operation completed successfully!
            </Alert>
          )}

          {showErrorMessage && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              {errorMessage}
            </Alert>
          )}

          {/* Data grid to display users */}
          <Paper className={classes.tablePaper} elevation={1}>
            {props.isGettingUsers ? (
              <div className={classes.loadingContainer}>
                <img src={spinner} width="32px" alt="Spinner" />
                <Typography style={{ marginLeft: "10px" }}>
                  Loading users...
                </Typography>
              </div>
            ) : props.getUsersError ? (
              <div className={classes.errorContainer}>
                <Alert severity="error">
                  Failed to load users. Please try refreshing the page.
                  {props.message && (
                    <div style={{ marginTop: "10px" }}>
                      Error details: {JSON.stringify(props.message)}
                    </div>
                  )}
                </Alert>
              </div>
            ) : props.users && props.users.length > 0 ? (
              <DataGrid
                className={classes.dataGrid}
                columns={columns}
                rows={props.users}
                components={{
                  Toolbar: GridToolbar,
                }}
                componentsProps={{
                  toolbar: {
                    csvOptions: { disableToolbarButton: true },
                    printOptions: { disableToolbarButton: true },
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                  pagination: {
                    SelectProps: {
                      native: false,
                      inputProps: { "aria-label": "Rows per page" },
                      MenuProps: {
                        disableScrollLock: true,
                        PaperProps: {
                          sx: { zIndex: 1500 },
                        },
                      },
                    },
                  },
                }}
                getRowId={(row) => row.username}
                autoHeight
                pagination
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                disableColumnMenu
                disableColumnSelector
              />
            ) : props.users && props.users.length === 0 ? (
              <div className={classes.errorContainer}>
                <Alert severity="info">
                  No users found. Click "Add User" to create your first user.
                </Alert>
              </div>
            ) : (
              <div className={classes.loadingContainer}>
                <img src={spinner} width="80px" alt="Spinner" />
                <Typography style={{ marginLeft: "10px" }}>
                  Initializing...
                </Typography>
              </div>
            )}
          </Paper>

          {/* Modal to Edit a user */}
          <Modal
            className={classes.modal}
            open={editDialogOpen}
            onClose={handleEditClose}
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
          >
            <div className={classes.paper}>
              <h2 id="simple-modal-title">Edit User</h2>
              {props.isUpdatingUser && (
                <div className={classes.loadingContainer}>
                  <img src={spinner} width="32px" alt="Spinner" />
                  <Typography style={{ marginLeft: "10px" }}>
                    Updating user...
                  </Typography>
                </div>
              )}
              {props.updateUserError && (
                <Alert severity="error" className="mt-3 mb-0">
                  Error while updating user:{" "}
                  {JSON.stringify(props.updateUserError)}
                </Alert>
              )}
              <form className={classes.form}>
                <TextField
                  id="firstname"
                  label="First Name"
                  variant="outlined"
                  required
                  fullWidth
                  margin="normal"
                  autoFocus
                  error={editFirstNameError !== ""}
                  helperText={editFirstNameError}
                  value={editedRowData ? editedRowData.firstName : ""}
                  onChange={(e) =>
                    setEditedRowData({
                      ...editedRowData,
                      firstName: e.target.value,
                    })
                  }
                />
                <TextField
                  id="lastname"
                  label="Last Name"
                  variant="outlined"
                  required
                  fullWidth
                  margin="normal"
                  autoFocus
                  error={editLastNameError !== ""}
                  helperText={editLastNameError}
                  value={editedRowData ? editedRowData.lastName : ""}
                  onChange={(e) =>
                    setEditedRowData({
                      ...editedRowData,
                      lastName: e.target.value,
                    })
                  }
                />
                <TextField
                  id="email"
                  label="Email"
                  variant="outlined"
                  required
                  fullWidth
                  margin="normal"
                  autoFocus
                  error={editEmailError !== ""}
                  helperText={editEmailError}
                  value={editedRowData ? editedRowData.email : ""}
                  onChange={handleEditEmailChange}
                />
                <FormControl variant="outlined" fullWidth margin="normal">
                  <InputLabel id="group-name">Group Name</InputLabel>
                  <Select
                    labelId="group-name"
                    id="group-name-select"
                    required
                    value={editedRowData ? editedRowData.newgroupname : ""}
                    onChange={(e) =>
                      setEditedRowData({
                        ...editedRowData,
                        newgroupname: e.target.value,
                      })
                    }
                    label="Group Name"
                  >
                    <MenuItem value={"Admin"}>Admin</MenuItem>
                    <MenuItem value={"PowerUser"}>Power User</MenuItem>
                    <MenuItem value={"Users"}>Users</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  onClick={handleUpdateUser}
                  disabled={props.isUpdatingUser}
                >
                  Save Changes
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setEditDialogOpen(false)}
                  style={{ margin: "10px 10px", alignSelf: "flex-start" }}
                >
                  Cancel
                </Button>
              </form>
            </div>
          </Modal>

          {/* Modal to add a user */}
          <Modal
            className={classes.modal}
            open={addDialogOpen}
            onClose={handleClose}
            aria-labelledby="simple-modal-title"
            aria-describedby="simple-modal-description"
          >
            <div className={classes.paper}>
              <h2 id="simple-modal-title">Add User</h2>
              {props.isAddingUser && (
                <div className={classes.loadingContainer}>
                  <img src={spinner} width="32px" alt="Spinner" />
                  <Typography style={{ marginLeft: "10px" }}>
                    Adding user...
                  </Typography>
                </div>
              )}
              {props.addUserError && (
                <Alert severity="error" className="mt-3 mb-0">
                  Error while adding user: {JSON.stringify(props.addUserError)}
                </Alert>
              )}
              <form className={classes.form} onSubmit={handleSubmit}>
                <TextField
                  id="firstname"
                  label="First Name"
                  variant="outlined"
                  required
                  fullWidth
                  margin="normal"
                  autoFocus
                  error={nameError !== ""}
                  helperText={nameError}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <TextField
                  id="lastname"
                  label="Last Name"
                  variant="outlined"
                  required
                  fullWidth
                  margin="normal"
                  autoFocus
                  error={nameError !== ""}
                  helperText={nameError}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
                <TextField
                  id="email"
                  label="Email"
                  variant="outlined"
                  required
                  fullWidth
                  margin="normal"
                  autoFocus
                  error={emailError !== ""}
                  helperText={emailError}
                  value={email}
                  onChange={handleEmailChange}
                />
                <TextField
                  id="password"
                  label="Password"
                  variant="outlined"
                  required
                  fullWidth
                  margin="normal"
                  type={showPassword ? "text" : "password"}
                  error={passwordError !== ""}
                  helperText={passwordError}
                  value={password}
                  onChange={handlePasswordChange}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    ),
                  }}
                />
                {password && <PasswordRequirements password={password} />}
                <FormControl variant="outlined" fullWidth margin="normal">
                  <InputLabel id="group-name">Group Name</InputLabel>
                  <Select
                    labelId="group-name"
                    id="group-name-select"
                    required
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    label="Group Name"
                  >
                    <MenuItem value={"Admin"}>Admin</MenuItem>
                    <MenuItem value={"PowerUser"}>Power User</MenuItem>
                    <MenuItem value={"Users"}>Users</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={props.isAddingUser}
                >
                  Add User
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setAddDialogOpen(false)}
                  style={{ margin: "10px 10px", alignSelf: "flex-start" }}
                >
                  Cancel
                </Button>
              </form>
            </div>
          </Modal>
        </div>
      ) : (
        <div>
          <h1>Not Authorized</h1>
        </div>
      )}
    </div>
  );
};

const mapStatetoProps = (state) => {
  const {
    isAddingUser,
    addUserError,
    isUpdatingUser,
    updateUserError,
    isUserDeleting,
    deleteUserError,
    isGettingUsers,
    getUsersError,
    users,
  } = state.cognitoUser;
  return {
    isAddingUser,
    addUserError,
    isUpdatingUser,
    updateUserError,
    isUserDeleting,
    deleteUserError,
    isGettingUsers,
    getUsersError,
    users,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    addUser: (user) => {
      dispatch(addUser(user));
    },
    getUser: () => {
      dispatch(getUser());
    },
    updateUser: (user) => {
      dispatch(updateCognitoUser(user));
    },
    deleteUser: (username) => {
      dispatch(deleteCognitoUser(username));
    },
  };
};

export default connect(mapStatetoProps, mapDispatchToProps)(Users);
